import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { dispatch } from '../index';

const initialState = {
  error: null,
  accounts: {
    content: []
  },
  accountInfo: {
    content: {},
    loadingAccountInfo: false,
    error: null
  }
};

const slice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
    },

    // GET ACCOUNTS
    getAccountsSuccess(state, action) {
      state.accounts = action.payload;
    },

    // SET ACCOUNT ID
    setAccountId(state, action) {
      state.accountId = action.payload;
    },

    // INIT ACCOUNT INFO
    initAccountInfo(state) {
      state.accountInfo = initialState.accountInfo;
    },

    // GET ACCOUNT INFO
    getAccountInfoPending(state) {
      state.accountInfo.loadingAccountInfo = true;
    },
    getAccountInfoSuccess(state, action) {
      state.accountInfo.content = action.payload;
      state.accountInfo.loadingAccountInfo = false;
    },
    getAccountInfoFail(state, action) {
      state.accountInfo.error = action.payload;
      state.accountInfo.loadingAccountInfo = false;
    },

    // GET ISP LIST
    getAccountIspListSuccess(state, action) {
      state.isps = action.payload;
    },
    getAccountIspListFail(state, action) {
      state.ispsError = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

export function getAccounts(search = '', type = null, guardianAccounts = false, page = 0, size = 50) {
  return async () => {
    try {
      const response = await axios.get(
        `/synergy/accounts?search=*${encodeURIComponent(
          search
        )}*&guardianAccounts=${guardianAccounts}&page=${page}&size=${size}&type=${type}`
      );
      dispatch(slice.actions.getAccountsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setAccountId(accountId) {
  return async () => {
    dispatch(slice.actions.setAccountId(accountId));
  };
}
export function initAccountInfo() {
  return async () => {
    dispatch(slice.actions.initAccountInfo());
  };
}

export function getAccountInfo(accountId, type) {
  return async () => {
    try {
      dispatch(slice.actions.getAccountInfoPending(accountId));
      const response = await axios.get(
        `/synergy/accounts/${accountId}?type=${type}&fields=ROLES&fields=ISPS&fields=MFA_USER_AGENTS&fields=MFA_METHODS`
      );
      dispatch(slice.actions.getAccountInfoSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getAccountInfoFail(error));
      throw error;
    }
  };
}

export function deleteAccountGuardianRole(accountId, type, role) {
  return async () => {
    try {
      await axios.delete(`/synergy/accounts/${accountId}/roles?role=${role}&type=${type}`);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateAccountGuardianRole(accountId, type, role) {
  return async () => {
    try {
      await axios.post(`/synergy/accounts/${accountId}/roles?type=${type}`, { roles: [role] });
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function setAccountIspList(ispList) {
  return async () => {
    dispatch(slice.actions.getAccountIspListSuccess({ content: ispList }));
  };
}

export function getAccountIspList() {
  return async () => {
    try {
      const response = await axios.get('/synergy/isps');
      dispatch(slice.actions.getAccountIspListSuccess(response?.data));
    } catch (error) {
      dispatch(slice.actions.getAccountIspListFail(error));
      throw error;
    }
  };
}

export function deleteAccountIsp(ispId, accountId) {
  return async () => {
    await axios.delete(`/synergy/isps/${ispId}/accounts/${accountId}/associated`);
  };
}

export function addAccountIsp(ispId, accountId, role) {
  return async () => {
    await axios.put(`/synergy/isps/${ispId}/accounts/${accountId}/associated`, { role: role });
  };
}

export function changeAccountPassword(accountId, password, currentPassword, type = null) {
  return async () => {
    await axios.post(`/synergy/accounts/${accountId}/passwords/change?type=${type}`, { newPassword: password, currentPassword });
    dispatch(getAccountInfo(accountId));
  };
}

export function deleteAccount(accountId, type) {
  return async () => {
    await axios.delete(`/synergy/accounts/${accountId}?type=${type}`);
  };
}

export function createAccount(account, type) {
  return async () => {
    const response = await axios.post(`/synergy/accounts?type=${type}`, account);
    dispatch(setAccountId(response.data.accountId));
    return response.data;
  };
}

// Devices bound to the account. Returns the Spring Data page ({ content, ... }).
export function getAccountInventory(accountId, type, page = 0, size = 100) {
  return async () => {
    const response = await axios.get(`/synergy/accounts/${accountId}/devices?type=${type}&page=${page}&size=${size}`);
    return response.data;
  };
}

// Claims a device into the given account's inventory (201 linked, 200 already linked, 409 linked elsewhere).
export function addAccountInventoryDevice(accountId, { serialNumber, uuid, description }, type) {
  return async () => {
    const response = await axios.post(`/synergy/accounts/${accountId}/devices?type=${type}`, { serialNumber, uuid, description });
    return response.data;
  };
}

// Removes only the account <-> device binding; the device becomes unclaimed.
export function deleteAccountInventoryDevice(accountId, serialNumber, type) {
  return async () => {
    await axios.delete(`/synergy/accounts/${accountId}/devices/${serialNumber}?type=${type}`);
  };
}
